import { useState, useEffect, useCallback, useMemo } from "react";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import {
  BellSimple,
  CheckCircle,
  WarningCircle,
  Info,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  NotificationItem,
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  isRead,
} from "shared/api/notifications";
import DashboardSection from "./DashboardSection";

const typeIcon = (type: NotificationItem["type"]) => {
  switch (type) {
    case "success":
      return <CheckCircle size={18} weight="duotone" className="text-status-good" />;
    case "error":
      return <WarningCircle size={18} weight="duotone" className="text-status-bad" />;
    default:
      return <Info size={18} weight="duotone" className="text-neutral-secondary-mid" />;
  }
};

/**
 * Lists the user's notifications (personal + broadcast). Supports marking a
 * single notification or all as read and filtering to unread only. Notifications
 * with a `url` expose an open-link action.
 */
const NotificationsListModule = () => {
  const { session } = useSession();
  const { user } = useUser();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);

  const load = useCallback(async () => {
    if (!session || !user?.id) return;
    setIsLoading(true);
    try {
      const data = await fetchNotifications(user.id, session);
      setNotifications(
        [...data].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
      );
    } catch (err) {
      console.error("Error loading notifications:", err);
      toast.error("Fout bij ophalen van meldingen");
    } finally {
      setIsLoading(false);
    }
  }, [session, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkRead = useCallback(
    async (id: number) => {
      if (!session || !user?.id) return;
      // optimistic
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, readByUsers: [...(n.readByUsers || []), user.id] }
            : n,
        ),
      );
      try {
        await markNotificationRead(id, user.id, session);
      } catch (err) {
        console.error("Error marking notification read:", err);
        toast.error("Kon melding niet markeren");
        load();
      }
    },
    [session, user?.id, load],
  );

  const handleMarkAllRead = useCallback(async () => {
    if (!session || !user?.id) return;
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, readByUsers: [...(n.readByUsers || []), user.id] })),
    );
    try {
      await markAllNotificationsRead(user.id, session);
      toast.success("Alle meldingen gemarkeerd als gelezen");
    } catch (err) {
      console.error("Error marking all read:", err);
      toast.error("Kon meldingen niet markeren");
      load();
    }
  }, [session, user?.id, load]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !isRead(n, user?.id)).length,
    [notifications, user?.id],
  );

  const displayed = useMemo(
    () =>
      showOnlyUnread
        ? notifications.filter((n) => !isRead(n, user?.id))
        : notifications,
    [notifications, showOnlyUnread, user?.id],
  );

  return (
    <DashboardSection
      title="Meldingen"
      description="Al je meldingen en acties"
      icon={<BellSimple size={22} weight="duotone" />}
      count={unreadCount}
      action={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowOnlyUnread((v) => !v)}
            className="text-xs font-medium text-moss-primary underline-offset-2 hover:underline"
          >
            {showOnlyUnread ? "Toon alle" : "Alleen ongelezen"}
          </button>
          <ButtonNew
            variant="secondaryNeutral"
            label="Alles als gelezen"
            onClick={handleMarkAllRead}
            isDisabled={unreadCount === 0}
          />
        </div>
      }
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-neutral-secondary-mid">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-moss-primary border-t-transparent" />
          Laden…
        </div>
      ) : displayed.length === 0 ? (
        <p className="py-4 text-sm text-neutral-secondary-mid">
          {showOnlyUnread ? "Geen ongelezen meldingen." : "Geen meldingen."}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-neutral-primary/10">
          {displayed.map((n) => {
            const read = isRead(n, user?.id);
            return (
              <li
                key={n.id}
                className={`flex items-start gap-3 py-3 ${read ? "opacity-60" : ""}`}
              >
                <div className="mt-0.5">{typeIcon(n.type)}</div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm text-neutral-primary ${read ? "" : "font-medium"}`}
                  >
                    {n.message}
                  </p>
                  {n.fileNames && n.fileNames.length > 0 && (
                    <p className="mt-0.5 text-xs text-neutral-secondary-mid">
                      Bestanden: {n.fileNames.join(", ")}
                    </p>
                  )}
                  <p className="mt-0.5 text-xs text-neutral-secondary-mid">
                    {new Date(n.createdAt).toLocaleString("nl-BE")}
                  </p>
                  <div className="mt-1 flex items-center gap-3">
                    {n.url && (
                      <a
                        href={n.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-moss-primary underline-offset-2 hover:underline"
                      >
                        <ArrowSquareOut size={14} weight="duotone" /> Openen
                      </a>
                    )}
                    {!read && (
                      <button
                        onClick={() => handleMarkRead(n.id)}
                        className="text-xs font-medium text-brick-primary underline-offset-2 hover:underline"
                      >
                        Markeer als gelezen
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </DashboardSection>
  );
};

export default NotificationsListModule;
